(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  root.TimeseekMarketCatalog = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  const DEFAULT_BASE_URL = 'https://timeseek.net';

  function normalizeUnicode(value) {
    const stringValue = (value || '').toString();

    try {
      return stringValue.normalize('NFKC');
    } catch (error) {
      return stringValue;
    }
  }

  function normalizeText(value) {
    return normalizeUnicode(value).replace(/\s+/g, ' ').trim();
  }

  function lowerCaseText(value) {
    return normalizeText(value).toLowerCase();
  }

  function decodeHtmlEntities(value) {
    return (value || '')
      .toString()
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&#x2F;/gi, '/')
      .replace(/&#47;/g, '/');
  }

  function stripTags(value) {
    return decodeHtmlEntities((value || '').toString().replace(/<[^>]*>/g, ' '));
  }

  function extractAttribute(openTag, attributeName) {
    const pattern = new RegExp(`\\b${attributeName}=(["'])([\\s\\S]*?)\\1`, 'i');
    const matched = (openTag || '').match(pattern);
    return matched ? decodeHtmlEntities(matched[2]) : '';
  }

  function extractMarketSectionId(html) {
    const matched = (html || '').match(/id=["']TSBrandTabPanelMarket-([^"']+)["']/i);
    return matched ? decodeHtmlEntities(matched[1]) : '';
  }

  function extractMarketGridFragment(html) {
    const source = (html || '').toString();
    const gridMarkerIndex = source.indexOf('data-ts-market-grid');
    if (gridMarkerIndex < 0) return '';

    const openTagStart = source.lastIndexOf('<', gridMarkerIndex);
    const openTagEnd = source.indexOf('>', gridMarkerIndex);
    if (openTagStart < 0 || openTagEnd < 0) return '';

    const contentStart = openTagEnd + 1;
    const emptyStateMarkerIndex = source.indexOf('data-ts-brand-market-empty-state', contentStart);
    if (emptyStateMarkerIndex < 0) return '';

    const contentEnd = source.lastIndexOf('<', emptyStateMarkerIndex);
    if (contentEnd <= contentStart) return '';

    return source.slice(contentStart, contentEnd);
  }

  function normalizeMarketReference(value) {
    const normalized = normalizeUnicode(value)
      .toUpperCase()
      .replace(/(?:^|\s)(?:REFERENCE|REF(?:ERENCE)?\.?|型番|品番|参照番号)\s*/g, ' ')
      .replace(/[#＃]/g, ' ')
      .replace(/[／/\\\-‐‑‒–—―ー_・･·・･.,:()\[\]{}]/g, ' ')
      .replace(/\s+/g, '')
      .replace(/[^A-Z0-9]/g, '');

    return normalized;
  }

  function collectReferenceCandidates(value) {
    const normalized = normalizeUnicode(value).toUpperCase();
    if (!normalized) return [];

    const candidates = [];
    const patterns = [
      /(?:REF(?:ERENCE)?\.?|型番|品番|参照番号)\s*[:：#＃]?\s*([A-Z0-9][A-Z0-9／/\\\-‐‑‒–—―ー_・･·.,()\[\] ]{3,31})/g,
      /(?:^|[^A-Z0-9])([A-Z]{0,4}\d[A-Z0-9／/\\\-‐‑‒–—―ー_・･·.,() ]{3,31})(?=$|[^A-Z0-9／/\\\-‐‑‒–—―ー_・･·.,() ])/g,
      /(?:^|[^A-Z0-9])(\d{2,4}(?:[./／\-‐‑‒–—―ー]\d{2,4}){2,}[A-Z0-9]*)(?=$|[^A-Z0-9./／\-‐‑‒–—―ー])/g,
      /(?:^|[^A-Z0-9])(\d{5,}[A-Z]{0,6})(?=$|[^A-Z0-9])/g,
    ];

    patterns.forEach((pattern) => {
      let matched;
      while ((matched = pattern.exec(normalized)) !== null) {
        const candidate = normalizeText(matched[1] || '');
        if (!candidate) continue;
        candidates.push(candidate.replace(/[)\]}]+$/, ''));
      }
    });

    return candidates;
  }

  function extractReferenceCandidate() {
    const values = Array.from(arguments);

    for (let index = 0; index < values.length; index += 1) {
      const currentValue = values[index];
      const candidates = collectReferenceCandidates(currentValue);
      if (candidates.length > 0) {
        return candidates[0];
      }

      const compactCandidate = normalizeText(currentValue).replace(/\s+/g, '');
      const compactDigits = compactCandidate.replace(/\D/g, '');
      if (compactDigits.length >= 5 && compactCandidate.length <= 32) {
        return compactCandidate;
      }
    }

    return '';
  }

  function canonicalizeMarketReference() {
    const candidate = extractReferenceCandidate.apply(null, arguments);
    return normalizeMarketReference(candidate);
  }

  function normalizeAffiliateTargetUrl(value) {
    const decoded = decodeHtmlEntities(value).trim();
    if (!decoded) return '';

    try {
      const url = new URL(decoded, DEFAULT_BASE_URL);
      const hostname = lowerCaseText(url.hostname);

      if (hostname.includes('hb.afl.rakuten.co.jp')) {
        const rakutenTarget = url.searchParams.get('pc') || url.searchParams.get('m');
        if (rakutenTarget) return normalizeAffiliateTargetUrl(rakutenTarget);
      }

      if (hostname.includes('ck.jp.ap.valuecommerce.com')) {
        const yahooTarget = url.searchParams.get('vc_url');
        if (yahooTarget) return normalizeAffiliateTargetUrl(yahooTarget);
      }

      Array.from(url.searchParams.keys()).forEach((key) => {
        if (/^utm_/i.test(key) || key === 'yclid' || key === 'scid') {
          url.searchParams.delete(key);
        }
      });
      url.hash = '';

      if (url.origin === DEFAULT_BASE_URL) {
        return `${url.pathname}${url.search}`;
      }

      return url.toString();
    } catch (error) {
      return decoded;
    }
  }

  function isInternalProductUrl(value) {
    const normalizedUrl = normalizeAffiliateTargetUrl(value);
    if (!normalizedUrl) return false;

    try {
      const parsed = new URL(normalizedUrl, DEFAULT_BASE_URL);
      return /^\/products\//.test(parsed.pathname);
    } catch (error) {
      return /^\/products\//.test(normalizedUrl);
    }
  }

  function buildMarketListingKey(record) {
    const stableUrl = normalizeAffiliateTargetUrl(record.productUrl || record.href || '');
    if (stableUrl) {
      return `url:${stableUrl}`;
    }

    const normalizedId = normalizeText(record.productId || '');
    if (normalizedId) {
      return `id:${normalizedId}`;
    }

    const source = lowerCaseText(record.source || record.sourceLabel || '');
    const canonicalReference = normalizeMarketReference(record.canonicalReference || record.rawReference || '');
    const searchText = lowerCaseText(record.searchText || '');

    if (!source && !canonicalReference && !searchText) {
      return '';
    }

    return `fallback:${source}:${canonicalReference}:${searchText}`;
  }

  function createEmptyStats() {
    return {
      requestedEntries: 0,
      fetchedPages: 0,
      mergedCards: 0,
      duplicateCards: 0,
      skippedNonMarketCards: 0,
      missingReferenceCount: 0,
      invalidReferenceCount: 0,
      failedPages: 0,
      retryableFailedPages: 0,
      nonRetryableFailedPages: 0,
      unresolvedRetryableEntries: 0,
    };
  }

  function extractMarketCardRecordsFromHtml(html, options) {
    const config = options || {};
    const stats = config.stats || null;
    const fragment = extractMarketGridFragment(html);
    if (!fragment) return [];

    const records = [];
    const cardPattern = /<(a|article)\b[\s\S]*?data-brand-product-card[\s\S]*?<\/\1>/gi;
    let matched;

    while ((matched = cardPattern.exec(fragment)) !== null) {
      const outerHtml = matched[0];
      const openTagMatch = outerHtml.match(/^<(a|article)\b([\s\S]*?)>/i);
      if (!openTagMatch) continue;

      const openTag = openTagMatch[0];
      const searchText = normalizeText(
        `${extractAttribute(openTag, 'data-search-text')} ${stripTags(outerHtml)}`
      );
      const rawReference =
        extractAttribute(openTag, 'data-ts-reference') ||
        extractAttribute(openTag, 'data-reference');
      const source = lowerCaseText(extractAttribute(openTag, 'data-ts-source'));
      const productUrl = extractAttribute(openTag, 'data-product-url') || extractAttribute(openTag, 'href');
      const href = extractAttribute(openTag, 'href');

      if (config.excludeTimeseek !== false) {
        if (source === 'timeseek' || isInternalProductUrl(productUrl || href)) {
          if (stats) {
            stats.skippedNonMarketCards += 1;
          }
          continue;
        }
      }

      const canonicalReference =
        normalizeMarketReference(extractAttribute(openTag, 'data-ts-canonical-reference')) ||
        canonicalizeMarketReference(rawReference, searchText);

      records.push({
        outerHtml,
        productId: extractAttribute(openTag, 'data-product-id'),
        productHandle: extractAttribute(openTag, 'data-product-handle'),
        productUrl,
        href,
        source,
        sourceLabel: extractAttribute(openTag, 'data-ts-source-label'),
        searchText,
        rawReference,
        canonicalReference,
      });
    }

    return records;
  }

  function mergeMarketCardRecords(recordGroups, options) {
    const logger = options && options.logger && typeof options.logger.warn === 'function' ? options.logger : null;
    const stats = createEmptyStats();
    const merged = [];
    const seenKeys = new Set();

    (recordGroups || []).forEach((group) => {
      (group.records || []).forEach((record) => {
        stats.mergedCards += 1;

        if (!record.rawReference) {
          stats.missingReferenceCount += 1;
        } else if (!record.canonicalReference) {
          stats.invalidReferenceCount += 1;
        }

        const listingKey = buildMarketListingKey(record);
        if (!listingKey) return;
        if (seenKeys.has(listingKey)) {
          stats.duplicateCards += 1;
          return;
        }

        seenKeys.add(listingKey);
        merged.push({
          listingKey,
          outerHtml: record.outerHtml,
          productId: record.productId,
          productHandle: record.productHandle,
          productUrl: record.productUrl,
          href: record.href,
          source: record.source,
          sourceLabel: record.sourceLabel,
          searchText: record.searchText,
          rawReference: record.rawReference,
          canonicalReference: record.canonicalReference,
        });
      });
    });

    if (
      logger &&
      (stats.duplicateCards > 0 || stats.missingReferenceCount > 0 || stats.invalidReferenceCount > 0)
    ) {
      logger.warn('ts market catalog warnings:', {
        duplicateCards: stats.duplicateCards,
        missingReferenceCount: stats.missingReferenceCount,
        invalidReferenceCount: stats.invalidReferenceCount,
      });
    }

    return {
      records: merged,
      html: merged.map((record) => record.outerHtml).join(''),
      stats,
    };
  }

  function applySharedSearchParams(rawUrl, sharedSearchParams) {
    const normalizedUrl = normalizeText(rawUrl);
    if (!normalizedUrl) return '';

    if (!sharedSearchParams || (typeof sharedSearchParams.size === 'number' && sharedSearchParams.size === 0)) {
      return normalizedUrl;
    }

    try {
      const url = new URL(normalizedUrl, DEFAULT_BASE_URL);
      sharedSearchParams.forEach((value, key) => {
        if (!url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      });

      if (url.origin === DEFAULT_BASE_URL) {
        return `${url.pathname}${url.search}`;
      }

      return url.toString();
    } catch (error) {
      return normalizedUrl;
    }
  }

  function buildSectionRequestUrl(rawUrl, sectionId, sharedSearchParams) {
    if (!rawUrl) return '';

    try {
      const url = new URL(rawUrl, DEFAULT_BASE_URL);
      if (sectionId) {
        url.searchParams.set('section_id', sectionId);
      }

      if (sharedSearchParams) {
        sharedSearchParams.forEach((value, key) => {
          if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
          }
        });
      }

      if (url.origin === DEFAULT_BASE_URL) {
        return `${url.pathname}${url.search}`;
      }

      return url.toString();
    } catch (error) {
      return rawUrl;
    }
  }

  function wait(delayMs) {
    return new Promise((resolve) => {
      setTimeout(resolve, Math.max(0, delayMs || 0));
    });
  }

  function getErrorStatus(error) {
    if (error && Number.isFinite(error.status)) {
      return Number(error.status);
    }

    const message = error && error.message ? error.message : '';
    const matched = message.match(/\bHTTP\s+(\d{3})\b/i);
    return matched ? parseInt(matched[1], 10) : null;
  }

  function isRetryableFetchError(error) {
    const status = getErrorStatus(error);
    if (!status) return false;
    return status === 408 || status === 425 || status === 429 || (status >= 500 && status < 600);
  }

  async function loadHtml(fetchHtml, targetUrl) {
    const result = await fetchHtml(targetUrl);

    if (typeof result === 'string') {
      return result;
    }

    if (result && typeof result.text === 'function') {
      return result.text();
    }

    if (result && typeof result.html === 'string') {
      return result.html;
    }

    throw new Error('Unsupported HTML response');
  }

  async function loadHtmlWithRetry(fetchHtml, targetUrl, options) {
    const config = options || {};
    const maxRetries = Math.max(0, Number(config.maxRetries) || 0);
    const retryDelayMs = Math.max(0, Number(config.retryDelayMs) || 0);
    let attempt = 0;

    while (true) {
      try {
        return await loadHtml(fetchHtml, targetUrl);
      } catch (error) {
        if (attempt >= maxRetries || !isRetryableFetchError(error)) {
          throw error;
        }

        attempt += 1;
        await wait(retryDelayMs * attempt);
      }
    }
  }

  async function mapWithConcurrency(items, concurrency, iteratee) {
    const queue = Array.isArray(items) ? items.slice() : [];
    const limit = Math.max(1, Number(concurrency) || 1);
    const workers = Array.from({ length: Math.min(limit, queue.length || 1) }, async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        await iteratee(item);
      }
    });

    await Promise.all(workers);
  }

  async function loadMarketCatalog(options) {
    const config = options || {};
    const fetchHtml = config.fetchHtml;
    const logger = config.logger && typeof config.logger.warn === 'function' ? config.logger : null;
    const entries = Array.isArray(config.entries) ? config.entries.filter(Boolean) : [];
    const stats = createEmptyStats();
    stats.requestedEntries = entries.length;

    if (typeof fetchHtml !== 'function' || entries.length === 0) {
      return { records: [], html: '', sectionId: '', stats };
    }

    const sharedSearchParams =
      config.sharedSearchParams instanceof URLSearchParams
        ? config.sharedSearchParams
        : new URLSearchParams(config.sharedSearchParams || '');
    const recordGroups = [];
    let sectionId = '';
    const retryOptions = {
      maxRetries: config.maxRetries == null ? 2 : config.maxRetries,
      retryDelayMs: config.retryDelayMs == null ? 250 : config.retryDelayMs,
    };
    const retryableEntries = new Map();
    const recoveryDelayMs = Math.max(0, Number(config.recoveryDelayMs) || 0);

    function getEntryRetryKey(entry, index) {
      return (
        normalizeText(entry && (entry.handle || entry.url || entry.href)) ||
        `entry-${index}`
      );
    }

    function markRetryableEntry(entry, index) {
      retryableEntries.set(getEntryRetryKey(entry, index), { entry, index });
    }

    function clearRetryableEntry(entry, index) {
      retryableEntries.delete(getEntryRetryKey(entry, index));
    }

    async function loadEntryRecords(entry, index) {
      let hadRetryableFailure = false;
      const pageUrls = []
        .concat(Array.isArray(entry.pageUrls) ? entry.pageUrls : [])
        .concat(Array.isArray(entry.pages) ? entry.pages : [])
        .map((pageUrl) => applySharedSearchParams(pageUrl, sharedSearchParams))
        .filter(Boolean);

      if (pageUrls.length > 0) {
        for (let pageIndex = 0; pageIndex < pageUrls.length; pageIndex += 1) {
          try {
            const html = await loadHtmlWithRetry(fetchHtml, pageUrls[pageIndex], retryOptions);
            stats.fetchedPages += 1;
            const records = extractMarketCardRecordsFromHtml(html, { excludeTimeseek: true, stats });
            recordGroups.push({ entry, records });
          } catch (error) {
            stats.failedPages += 1;
            if (isRetryableFetchError(error)) {
              stats.retryableFailedPages += 1;
              hadRetryableFailure = true;
            } else {
              stats.nonRetryableFailedPages += 1;
            }
            if (logger) {
              logger.warn('ts market catalog page fetch failed:', {
                url: pageUrls[pageIndex],
                message: error && error.message ? error.message : String(error),
              });
            }
          }
        }

        if (hadRetryableFailure) {
          markRetryableEntry(entry, index);
        } else {
          clearRetryableEntry(entry, index);
        }
        return;
      }

      const baseUrl = applySharedSearchParams(entry.url || entry.href || '', sharedSearchParams);
      if (!baseUrl) return;

      if (index === 0) {
        try {
          const html = await loadHtmlWithRetry(fetchHtml, baseUrl, retryOptions);
          stats.fetchedPages += 1;
          sectionId = extractMarketSectionId(html) || sectionId;
          const records = extractMarketCardRecordsFromHtml(html, { excludeTimeseek: true, stats });
          recordGroups.push({ entry, records });
          clearRetryableEntry(entry, index);
          return;
        } catch (error) {
          stats.failedPages += 1;
          if (isRetryableFetchError(error)) {
            stats.retryableFailedPages += 1;
            hadRetryableFailure = true;
          } else {
            stats.nonRetryableFailedPages += 1;
          }
          if (logger) {
            logger.warn('ts market catalog discovery fetch failed:', {
              url: baseUrl,
              message: error && error.message ? error.message : String(error),
            });
          }
        }
      }

      if (sectionId) {
        const sectionUrl = buildSectionRequestUrl(baseUrl, sectionId, sharedSearchParams);
        try {
          const html = await loadHtmlWithRetry(fetchHtml, sectionUrl, retryOptions);
          stats.fetchedPages += 1;
          const records = extractMarketCardRecordsFromHtml(html, { excludeTimeseek: true, stats });
          if (records.length > 0) {
            recordGroups.push({ entry, records });
            clearRetryableEntry(entry, index);
            return;
          }
        } catch (error) {
          stats.failedPages += 1;
          if (isRetryableFetchError(error)) {
            stats.retryableFailedPages += 1;
            hadRetryableFailure = true;
          } else {
            stats.nonRetryableFailedPages += 1;
          }
          if (logger) {
            logger.warn('ts market catalog section fetch failed:', {
              url: sectionUrl,
              message: error && error.message ? error.message : String(error),
            });
          }
        }
      }

      try {
        const html = await loadHtmlWithRetry(fetchHtml, baseUrl, retryOptions);
        stats.fetchedPages += 1;
        const records = extractMarketCardRecordsFromHtml(html, { excludeTimeseek: true, stats });
        recordGroups.push({ entry, records });
        clearRetryableEntry(entry, index);
      } catch (error) {
        stats.failedPages += 1;
        if (isRetryableFetchError(error)) {
          stats.retryableFailedPages += 1;
          hadRetryableFailure = true;
        } else {
          stats.nonRetryableFailedPages += 1;
        }
        if (logger) {
          logger.warn('ts market catalog fallback fetch failed:', {
            url: baseUrl,
            message: error && error.message ? error.message : String(error),
          });
        }
      }

      if (hadRetryableFailure) {
        markRetryableEntry(entry, index);
      } else {
        clearRetryableEntry(entry, index);
      }
    }

    const [firstEntry, ...remainingEntries] = entries;

    if (firstEntry) {
      await loadEntryRecords(firstEntry, 0);
    }

    await mapWithConcurrency(
      remainingEntries.map((entry, index) => ({ entry, index: index + 1 })),
      config.concurrency || 4,
      async (payload) => {
        await loadEntryRecords(payload.entry, payload.index);
      }
    );

    if (retryableEntries.size > 0) {
      if (recoveryDelayMs > 0) {
        await wait(recoveryDelayMs);
      }

      await mapWithConcurrency(
        Array.from(retryableEntries.values()),
        config.recoveryConcurrency || 1,
        async (payload) => {
          await loadEntryRecords(payload.entry, payload.index);
        }
      );
    }

    const merged = mergeMarketCardRecords(recordGroups, { logger });
    return {
      records: merged.records,
      html: merged.html,
      sectionId,
      stats: {
        requestedEntries: stats.requestedEntries,
        fetchedPages: stats.fetchedPages,
        mergedCards: merged.records.length,
        duplicateCards: merged.stats.duplicateCards,
        skippedNonMarketCards: stats.skippedNonMarketCards,
        missingReferenceCount: merged.stats.missingReferenceCount,
        invalidReferenceCount: merged.stats.invalidReferenceCount,
        failedPages: stats.failedPages,
        retryableFailedPages: stats.retryableFailedPages,
        nonRetryableFailedPages: stats.nonRetryableFailedPages,
        unresolvedRetryableEntries: retryableEntries.size,
      },
    };
  }

  return {
    normalizeMarketReference,
    extractReferenceCandidate,
    canonicalizeMarketReference,
    normalizeAffiliateTargetUrl,
    buildMarketListingKey,
    extractMarketSectionId,
    extractMarketGridFragment,
    extractMarketCardRecordsFromHtml,
    mergeMarketCardRecords,
    loadMarketCatalog,
  };
});
